import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import moment from "moment";
import { Utils } from "formiojs";
import { Box, Typography, Divider } from "@mui/material";
import { CalendarToday as CalendarIcon } from "@mui/icons-material";

import * as Constants from "common/constants";
import * as General from "common/general";
import { logger } from "helpers/logger";
import { fetchgetAllFroms } from "../../store/thunks/formThunks";
//import db from "indexedDB";

import DataContainer from "../../components/common/DataContainer";
import CustomPagination from "../../components/common/CustomPagination";
import SearchFilterBar from "../../components/common/SearchFilterBar";
import Loader from "../../components/common/custom-component/Loader";
import EnhancedConfirmationDialog from "../../components/common/custom-component/EnhancedConfirmationDialog";
import { useTableColumns } from "../../hooks/useTableColumns";
import { useToast } from "../../components/common/custom-component/ToastProvider";

const ManageForms = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [t] = useTranslation();
    const { success: ToastSuccess, error: ToastError } = useToast();

    const [state, setState] = useState({
        forms: [],
        isLoading: false,
        currentPage: 1,
        recordsPerPage: 10,
        totalPages: 1, // Discovery starts with 1
        searchInput: "",
        deleteDialog: {
            open: false,
            record: null,
        }
    });

    // #region ONLINE PAGINATION
    useEffect(() => {
        const loadPageData = async () => {
            setState(prev => ({ ...prev, isLoading: true }));
            try {
                const result = await dispatch(fetchgetAllFroms({
                    pageNumber: state.currentPage,
                    recordsPerPage: state.recordsPerPage
                })).unwrap();

                const returnedPage = result.currentPageNumber;
                const isFullPage = result.formData?.length === state.recordsPerPage;

                if (returnedPage !== state.currentPage && state.currentPage !== 1) {
                    setState(prev => ({
                        ...prev,
                        currentPage: prev.currentPage - 1,
                        totalPages: prev.currentPage - 1,
                        isLoading: false
                    }));
                    return;
                }

                const sortedForms = [...(result.formData || [])].sort(
                    (a, b) => new Date(b.modified) - new Date(a.modified)
                );

                setState(prev => ({
                    ...prev,
                    forms: sortedForms,
                    // Discover one more page if current is full
                    totalPages: isFullPage ? Math.max(prev.totalPages, state.currentPage + 1) : state.currentPage,
                    isLoading: false,
                }));
            } catch (err) {
                logger.error("Error loading forms page:", err);
                ToastError(t("An error occured while loading page data."));
                setState(prev => ({ ...prev, isLoading: false }));
            }
        };

        loadPageData();
    }, [dispatch, state.currentPage, state.recordsPerPage, t, ToastError]);
    // #endregion

    // #region SEARCH & PAGINATION
    const handlePageChange = (event, value) => {
        setState(prev => ({ ...prev, currentPage: value }));
    };

    const handleSearchChange = (value) => {
        setState(prev => ({ ...prev, searchInput: value }));
    };

    const filteredData = useMemo(() => {
        if (!state.searchInput) return state.forms;
        const query = state.searchInput.toLowerCase();
        return state.forms.filter(f =>
            (f.title || "").toLowerCase().includes(query) ||
            (f.name || "").toLowerCase().includes(query) ||
            (f.path || "").toLowerCase().includes(query)
        );
    }, [state.forms, state.searchInput]);
    // #endregion

    // #region ACTIONS
    const handleView = useCallback((row) => {
        window.open(`/formView/${row._id}`);
    }, []);

    const handleEdit = useCallback((row) => {
        window.open(`/formedit/${row._id}`);
    }, []);

    const handleDeleteClick = (row) => {
        setState(prev => ({
            ...prev,
            deleteDialog: { open: true, record: row }
        }));
    };

    const handleCancelDelete = () => {
        setState(prev => ({
            ...prev,
            deleteDialog: { open: false, record: null }
        }));
    };

    const handleConfirmDelete = async () => {
        handleCancelDelete();
    };

    const handleDuplicate = (form) => {
        const formData = {
            title: moment().format("hhmmss").toString() + form.name,
            display: form.display,
            type: form.type,
            name: moment().format("hhmmss").toString() + form.name,
            path: (moment().format("hhmmss").toString() + form.name).toString().replaceAll(" ", ""),
            components: form.components,
            settings: {
                languages: form.settings?.languages,
                translationUrl: form.settings?.translationUrl,
            },
        };

        General.PostToEndPoint(
            Constants.CreateFormEndPoint,
            null,
            JSON.stringify(formData),
            (res) => {
                if (res.status === Constants.SuccessStatus) {
                    ToastSuccess(t("Form duplicated successfully"));
                    window.location.reload();
                }
            },
            (err) => logger.error("Duplicate failed", err)
        );
    };

    const handleExport = (form) => {
        const blob = new Blob([JSON.stringify(form, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${form.name}.json`;
        a.click();
        URL.revokeObjectURL(url);
    };
    // #endregion

    // #region TABLE COLUMNS
    const actions = useMemo(() => ({
        onView: handleView,
        onEdit: handleEdit,
        onDelete: handleDeleteClick,
        onDuplicate: handleDuplicate,
        onExport: handleExport,
    }), [handleView, handleEdit]);

    const { columns } = useTableColumns('ManageForms', { actions });
    // #endregion

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                <Typography variant="h3" color="primary">
                    {t("Manage Forms")}
                </Typography>
                {state.isLoading && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <Loader variant="circular" message={t("Loading page...")} size="small" />
                    </Box>
                )}
            </Box>

            <Typography variant="body1" color="text.secondary" paragraph>
                {t("This is a Manage Forms page.")}
            </Typography>

            <Divider sx={{ my: 3 }} />

            {!state.forms || state.forms.length === 0 ? (
                state.isLoading ? null : (
                    <DataContainer
                        data={[]}
                        columns={columns}
                        emptyStateIcon={CalendarIcon}
                        emptyStateTitle={t("No Forms found")}
                        emptyStateSubtitle={t("Start adding forms to your company")}
                    />
                )
            ) : (
                <>
                    <Box sx={{ mb: 2, display: "flex", flexDirection: "column", gap: 2 }}>
                        <SearchFilterBar
                            mode="frontend"
                            searchValue={state.searchInput}
                            onSearchChange={handleSearchChange}
                            showFilterButton={false}
                            showSearchButton={false}
                            searchPlaceholder={t("Search forms on this page...")}
                        />
                    </Box>

                    <DataContainer
                        data={filteredData}
                        columns={columns}
                        showRowNumbers
                        maxHeight={600}
                        stickyHeader
                    />

                    <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                        <CustomPagination
                            currentPage={state.currentPage}
                            totalPages={state.totalPages}
                            onPageChange={handlePageChange}
                            showFirstButton
                            showLastButton
                        />
                    </Box>
                </>
            )}

            <EnhancedConfirmationDialog
                open={state.deleteDialog.open}
                onClose={handleCancelDelete}
                onConfirm={handleConfirmDelete}
                title={t("Confirm Delete")}
                message={t("Are you sure you want to remove this form? This action cannot be undone.")}
                confirmText={t("DELETE")}
                confirmColor="error"
                cancelText={t("Cancel")}
            />
        </Box>
    );
};

export default ManageForms;
